import React, { useEffect, useMemo, useState } from "react";
import {
  getAllUsers,
  approveUser,
  updateUserStatus,
  updateUserProfile,
  updateClubStatusAction,
  deleteUserAction,
} from "actions/action";
import { searchUsers } from "actions/action";
import { adminCreateUser, deleteAllTempUsers } from "actions/action";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import * as XLSX from "xlsx";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [approving, setApproving] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    phone: "",
    first_name: "",
    last_name: "",
    admission_no: "",
    club_id: "",
  });
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    admission_no: "",
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    password: "",
  });
  const [creating, setCreating] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ success: 0, failed: 0, total: 0 });
  const [downloading, setDownloading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getAllUsers();
        if (!mounted) return;
        if (res?.data) {
          const data = res.data;
          const normalized = Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
              ? data.data
              : Array.isArray(data?.users)
                ? data.users
                : [];
          const onlyStudents = normalized.filter(
            (u) => (u?.role || "").toLowerCase() === "student"
          );
          setUsers(onlyStudents);
        } else if (res?.err) {
          setError("Failed to load users");
        }
      } catch {
        if (mounted) setError("Failed to load users");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Debounced search
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length === 0) return;
    setSearching(true);
    const id = setTimeout(async () => {
      try {
        const res = await searchUsers(trimmed);
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.users)
            ? res.users
            : [];
        const onlyStudents = list.filter(
          (u) => (u?.role || "").toLowerCase() === "student"
        );
        setUsers(onlyStudents);
        setSelectedIds([]); // Clear selection on search
      } catch {
        // ignore
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(id);
  }, [query]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(users.map(u => u.user_id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (userId) => {
    setSelectedIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleApprove = async (userId = null) => {
    const idsToProcess = userId ? [userId] : selectedIds;
    if (idsToProcess.length === 0) return;

    // Set approving state for all ids
    const newApproving = {};
    idsToProcess.forEach(id => { newApproving[id] = true; });
    setApproving(s => ({ ...s, ...newApproving }));

    // Optimistic update
    setUsers(prev => prev.map(u => idsToProcess.includes(u.user_id) ? { ...u, is_active: true } : u));

    try {
      // If single ID provided directly (button click), pass single ID. If bulk, pass array.
      // But action now supports array for both.
      const res = await approveUser(idsToProcess);

      if (res?.err) {
        // Revert on error
        setUsers(prev => prev.map(u => idsToProcess.includes(u.user_id) ? { ...u, is_active: false } : u));
        setError("Approval failed. Please try again.");
      } else {
        setSelectedIds([]); // Clear selection on success
      }
    } catch {
      setUsers(prev => prev.map(u => idsToProcess.includes(u.user_id) ? { ...u, is_active: false } : u));
      setError("Approval failed. Please try again.");
    } finally {
      const resetApproving = {};
      idsToProcess.forEach(id => { resetApproving[id] = false; });
      setApproving(s => ({ ...s, ...resetApproving }));
    }
  };

  const handleDeactivate = async (userId = null) => {
    const idsToProcess = userId ? [userId] : selectedIds;
    if (idsToProcess.length === 0) return;

    // Set approving state for all ids
    const newApproving = {};
    idsToProcess.forEach(id => { newApproving[id] = true; });
    setApproving(s => ({ ...s, ...newApproving }));

    // Optimistic update
    setUsers(prev => prev.map(u => idsToProcess.includes(u.user_id) ? { ...u, is_active: false } : u));

    try {
      const res = await updateUserStatus(idsToProcess, false);
      if (res?.err) {
        // Revert on error
        setUsers(prev => prev.map(u => idsToProcess.includes(u.user_id) ? { ...u, is_active: true } : u));
        setError("Deactivation failed. Please try again.");
      } else {
        setSelectedIds([]); // Clear selection on success
      }
    } catch {
      setUsers(prev => prev.map(u => idsToProcess.includes(u.user_id) ? { ...u, is_active: true } : u));
      setError("Deactivation failed. Please try again.");
    } finally {
      const resetApproving = {};
      idsToProcess.forEach(id => { resetApproving[id] = false; });
      setApproving(s => ({ ...s, ...resetApproving }));
    }
  };

  const startEdit = (u) => {
    setEditingId(u.user_id);
    setEditForm({
      phone: u.phone || "",
      first_name: u.first_name || "",
      last_name: u.last_name || "",
      admission_no: u.admission_no || "",
      club_id: u.club_id ?? "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({
      phone: "",
      first_name: "",
      last_name: "",
      admission_no: "",
      club_id: "",
    });
  };

  const saveEdit = async (userId) => {
    try {
      const payload = {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        phone: editForm.phone,
        admission_no: editForm.admission_no,
        club_id: editForm.club_id !== "" ? Number(editForm.club_id) : undefined,
      };
      const res = await updateUserProfile(userId, payload);
      if (res?.err) throw new Error();
      setUsers((prev) =>
        prev.map((u) => (u.user_id === userId ? { ...u, ...payload, club_status: u.club_id !== payload.club_id ? 'pending' : u.club_status } : u))
      );
      cancelEdit();
    } catch {
      setError("Failed to update user profile");
    }
  };

  const handleClubStatusUpdate = async (userId, status) => {
    const res = await updateClubStatusAction(userId, status);
    if (res && res.success) {
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, club_status: status } : u));
    } else {
      setError("Failed to update club status");
    }
  };

  const validateCreate = () => {
    if (
      !createForm.admission_no ||
      !createForm.firstname ||
      !createForm.lastname ||
      !createForm.email ||
      !createForm.phone ||
      !createForm.password
    ) {
      setValidationError("All fields are required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.email)) {
      setValidationError("Enter a valid email");
      return false;
    }
    if (!/^[6789]\d{9}$/.test(createForm.phone)) {
      setValidationError("Enter a valid 10-digit mobile number");
      return false;
    }
    if (createForm.password.length < 6) {
      setValidationError("Password must be at least 6 characters");
      return false;
    }
    setValidationError("");
    return true;
  };

  const handleCreate = async () => {
    if (!validateCreate()) return;
    setCreating(true);
    try {
      const payload = {
        email: createForm.email,
        phone: createForm.phone,
        role: "student",
        password: createForm.password,
        first_name: createForm.firstname,
        last_name: createForm.lastname,
        admission_no: createForm.admission_no,
      };
      const res = await adminCreateUser(payload);
      if (res?.success || res?.message) {
        // refresh list
        const fresh = await getAllUsers();
        const data = fresh?.data;
        const normalized = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.users)
              ? data.users
              : [];
        const onlyStudents = normalized.filter(
          (u) => (u?.role || "").toLowerCase() === "student"
        );
        setUsers(onlyStudents);
        setShowCreate(false);
        setCreateForm({
          admission_no: "",
          firstname: "",
          lastname: "",
          email: "",
          phone: "",
          password: "",
        });
      } else if (res?.err) {
        setValidationError("Failed to create user");
      } else if (res && res.success === false && res.message) {
        setValidationError(res.message);
      } else {
        setValidationError("Failed to create user");
      }
    } catch {
      setValidationError("Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'csv' && fileExtension !== 'xlsx') {
      setValidationError("Please select only CSV or XLSX files");
      e.target.value = '';
      return;
    }

    setSelectedFile(file);
    setValidationError("");
  };

  const parseFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (error) {
          reject(new Error("Error parsing file: " + error.message));
        }
      };

      reader.onerror = () => {
        reject(new Error("Error reading file"));
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://api.nvcti.in/user/download-report', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'student_report.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download error:", error);
      alert("Failed to download report. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      setValidationError("Please select a file first");
      return;
    }

    setUploading(true);
    setUploadProgress({ success: 0, failed: 0, total: 0 });
    setValidationError("");

    try {
      // Create FormData and append the file
      const formData = new FormData();
      formData.append('file', selectedFile);

      // Get the token from localStorage
      const token = localStorage.getItem('token');

      // Make the API call to the bulk-signup endpoint
      const response = await fetch('https://api.nvcti.in/user/bulk-signup', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Upload failed');
      }

      // Refresh user list
      const fresh = await getAllUsers();
      const data = fresh?.data;
      const normalized = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.users)
            ? data.users
            : [];
      const onlyStudents = normalized.filter(
        (u) => (u?.role || "").toLowerCase() === "student"
      );
      setUsers(onlyStudents);

      // Show success message
      setValidationError(result.message || "Successfully uploaded users!");

      // Reset file input
      setSelectedFile(null);
      const fileInput = document.getElementById('bulk-upload-file');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      setValidationError("Error uploading file: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      const res = await deleteUserAction(userToDelete.user_id);
      if (res?.success) {
        setUsers(users.filter(u => u.user_id !== userToDelete.user_id));
        setShowDeleteModal(false);
        setUserToDelete(null);
      } else {
        setError(res?.message || "Failed to delete user");
      }
    } catch {
      setError("Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = (u) => {
    setUserToDelete(u);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };
  return (
    <>
      <div className="relative">
        <div className="flex justify-between items-center px-4 mt-6">
          <p className="font-semibold text-lg text-white">User Details</p>
        </div>

        <div className="px-6 mt-4 mb-6">
          <form className="flex flex-row flex-wrap items-center">
            <div className="relative flex w-full flex-wrap items-stretch">
              <span className="z-10 h-full leading-snug font-normal absolute text-center text-white bg-transparent rounded text-base items-center justify-center w-8 pl-3 py-3">
                <i className="fas fa-search"></i>
              </span>
              <Tooltip title="Search Record" arrow>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search students by name, phone, email, admission no"
                  className="border-0 px-3 py-3 placeholder-blueGray-400 text-white relative bg-blueGray-700 rounded text-sm shadow outline-none focus:outline-none focus:ring w-full pl-10 pr-10"
                />
              </Tooltip>
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-0 top-0 bottom-0 flex items-center justify-center w-10 text-white hover:text-blueGray-300 focus:outline-none"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
              {searching && (
                <div className="absolute right-12 top-1/2 -translate-y-1/2 text-xs text-blueGray-300">
                  Searching…
                </div>
              )}
              <Tooltip title="Create User" arrow>
                <button
                  onClick={() => setShowCreate((s) => !s)}
                  className="bg-lightBlue-500 text-white active:bg-lightBlue-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ml-4 ease-linear transition-all duration-150"
                  type="button"
                >
                  {showCreate ? "Hide Form" : "Create User"}
                </button>
              </Tooltip>
            </div>
          </form>

          {/* Bulk Upload Section */}
          <div className="mt-4">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <div className="flex items-center gap-2">
                <label htmlFor="bulk-upload-file" className="text-white text-sm font-semibold cursor-pointer">
                  Select File (CSV/XLSX):
                </label>
                <input
                  id="bulk-upload-file"
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => document.getElementById('bulk-upload-file')?.click()}
                  className="bg-blueGray-600 text-white active:bg-blueGray-700 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
                >
                  Choose File
                </button>
                {selectedFile && (
                  <span className="text-white text-sm ml-2">
                    {selectedFile.name}
                  </span>
                )}
              </div>
              <Tooltip title="Upload users from Excel/CSV file" arrow>
                <button
                  onClick={handleBulkUpload}
                  disabled={!selectedFile || uploading}
                  className="bg-emerald-500 text-white active:bg-emerald-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                  type="button"
                >
                  {uploading ? "Uploading..." : "Bulk Upload"}
                </button>
              </Tooltip>
              <Tooltip title="Download Student Report" arrow>
                <button
                  onClick={handleDownloadReport}
                  disabled={downloading}
                  className="bg-indigo-500 text-white active:bg-indigo-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed ml-4"
                  type="button"
                >
                  {downloading ? "Downloading..." : "Download Report"}
                </button>
              </Tooltip>
            </div>
            {validationError && (
              <div className={`text-sm mt-2 ${validationError.includes('Successfully') ? 'text-green-400' : 'text-red-400'}`}>
                {validationError}
              </div>
            )}
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="px-6 mb-4 flex gap-3 animate-pulse">
            <button
              onClick={() => handleApprove()}
              className="bg-emerald-600 text-white font-bold text-xs uppercase px-4 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
            >
              Approve Selected ({selectedIds.length})
            </button>
            <button
              onClick={() => handleDeactivate()}
              className="bg-red-600 text-white font-bold text-xs uppercase px-4 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
            >
              Deactivate Selected ({selectedIds.length})
            </button>
          </div>
        )}

        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-blueGray-700">
          <div className="block w-full overflow-x-auto">
            <div className="rounded-t mb-0 px-4 py-3 border-0">
              <div className="flex flex-wrap items-center">
                <div className="w-full px-4 mt-3 flex gap-3 items-center">
                </div>
              </div>
            </div>
            {showCreate && (
              <div className="px-4 pb-2">
                <div className="bg-blueGray-800 border border-blueGray-600 rounded p-4">
                  <h4 className="text-white font-semibold mb-3">
                    New User
                  </h4>
                  {validationError && (
                    <div className="mb-3 text-red-500 text-sm">
                      {validationError}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs mb-1">Admission No</label>
                      <input
                        className="w-full border rounded px-3 py-2 bg-blueGray-800 text-white border-blueGray-600"
                        value={createForm.admission_no}
                        onChange={(e) =>
                          setCreateForm((s) => ({
                            ...s,
                            admission_no: e.target.value,
                          }))
                        }
                        placeholder="Enter admission number"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">First Name</label>
                      <input
                        className="w-full border rounded px-3 py-2 bg-blueGray-800 text-white border-blueGray-600"
                        value={createForm.firstname}
                        onChange={(e) =>
                          setCreateForm((s) => ({
                            ...s,
                            firstname: e.target.value,
                          }))
                        }
                        placeholder="Enter first name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">Last Name</label>
                      <input
                        className="w-full border rounded px-3 py-2 bg-blueGray-800 text-white border-blueGray-600"
                        value={createForm.lastname}
                        onChange={(e) =>
                          setCreateForm((s) => ({
                            ...s,
                            lastname: e.target.value,
                          }))
                        }
                        placeholder="Enter last name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">Email</label>
                      <input
                        className="w-full border rounded px-3 py-2 bg-blueGray-800 text-white border-blueGray-600"
                        value={createForm.email}
                        onChange={(e) =>
                          setCreateForm((s) => ({
                            ...s,
                            email: e.target.value,
                          }))
                        }
                        placeholder="Enter email"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">
                        Mobile Number
                      </label>
                      <input
                        className="w-full border rounded px-3 py-2 bg-blueGray-800 text-white border-blueGray-600"
                        value={createForm.phone}
                        onChange={(e) =>
                          setCreateForm((s) => ({
                            ...s,
                            phone: e.target.value,
                          }))
                        }
                        placeholder="Enter mobile number"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1">Password</label>
                      <input
                        type="password"
                        className="w-full border rounded px-3 py-2 bg-blueGray-800 text-white border-blueGray-600"
                        value={createForm.password}
                        onChange={(e) =>
                          setCreateForm((s) => ({
                            ...s,
                            password: e.target.value,
                          }))
                        }
                        placeholder="Enter password"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      onClick={() => setShowCreate(false)}
                      className="bg-blueGray-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreate}
                      disabled={creating}
                      className="bg-blueGray-800 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md disabled:opacity-60"
                    >
                      {creating ? "Creating..." : "Create User"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="block w-full overflow-x-auto p-4">
              {loading && <div className="text-white">Loading...</div>}
              {!!error && !loading && (
                <div className="text-red-400">{error}</div>
              )}
              {!loading &&
                !error &&
                Array.isArray(users) &&
                users.length > 0 && (
                  <table className="items-center w-full bg-transparent border-collapse">
                    <thead>
                      <tr>
                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                          <input
                            type="checkbox"
                            checked={users.length > 0 && selectedIds.length === users.length}
                            onChange={handleSelectAll}
                            className="form-checkbox h-5 w-5 text-emerald-500 rounded border-gray-300 bg-white transition duration-150 ease-in-out"
                          />
                        </th>
                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                          Phone
                        </th>
                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                          First Name
                        </th>
                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                          Last Name
                        </th>
                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                          Admission No
                        </th>
                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                          Email
                        </th>
                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                          Role
                        </th>
                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                          Club Name
                        </th>
                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                          Club Status
                        </th>
                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                          Status
                        </th>
                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id}>
                          <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(u.user_id)}
                              onChange={() => handleSelectRow(u.user_id)}
                              className="form-checkbox h-5 w-5 text-emerald-500 rounded border-gray-300 bg-white transition duration-150 ease-in-out"
                            />
                          </td>
                          <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                            {editingId === u.user_id ? (
                              <input
                                className="border px-2 py-1 rounded text-sm w-40 bg-blueGray-800 text-white"
                                value={editForm.phone}
                                onChange={(e) =>
                                  setEditForm((s) => ({
                                    ...s,
                                    phone: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              u.phone || "-"
                            )}
                          </td>
                          <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                            {editingId === u.user_id ? (
                              <input
                                className="border px-2 py-1 rounded text-sm w-32 bg-blueGray-800 text-white"
                                value={editForm.first_name}
                                onChange={(e) =>
                                  setEditForm((s) => ({
                                    ...s,
                                    first_name: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              u.first_name || "-"
                            )}
                          </td>
                          <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                            {editingId === u.user_id ? (
                              <input
                                className="border px-2 py-1 rounded text-sm w-32 bg-blueGray-800 text-white"
                                value={editForm.last_name}
                                onChange={(e) =>
                                  setEditForm((s) => ({
                                    ...s,
                                    last_name: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              u.last_name || "-"
                            )}
                          </td>
                          <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                            {editingId === u.user_id ? (
                              <input
                                className="border px-2 py-1 rounded text-sm w-32 bg-blueGray-800 text-white"
                                value={editForm.admission_no}
                                onChange={(e) =>
                                  setEditForm((s) => ({
                                    ...s,
                                    admission_no: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              u.admission_no || "-"
                            )}
                          </td>
                          <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                            {u.email || "-"}
                          </td>
                          <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                            {u.role || "-"}
                          </td>
                          <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                            {u.club_name || "-"}
                          </td>
                          <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                            {u.club_id ? (
                              <div className="flex flex-col gap-1">
                                <span className={`px-2 py-1 rounded text-xs text-white w-fit ${u.club_status === 'approved' ? 'bg-emerald-500' :
                                  u.club_status === 'rejected' ? 'bg-red-500' :
                                    'bg-orange-500'
                                  }`}>
                                  {u.club_status || 'Pending'}
                                </span>
                                {u.club_status !== 'approved' && u.club_status !== 'rejected' && (
                                  <div className="flex gap-1 mt-1">
                                    <button
                                      onClick={() => handleClubStatusUpdate(u.user_id, 'approved')}
                                      className="bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded hover:bg-emerald-500"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => handleClubStatusUpdate(u.user_id, 'rejected')}
                                      className="bg-red-600 text-white text-[10px] px-2 py-0.5 rounded hover:bg-red-500"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : "-"}
                          </td>
                          <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                            {u.is_active ? "Active" : "Inactive"}
                          </td>
                          <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 text-center flex gap-4">
                            {editingId === u.user_id ? (
                              <>
                                <button
                                  onClick={() => saveEdit(u.user_id)}
                                  className="bg-emerald-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md mr-2"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="bg-blueGray-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md mr-2"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                {!u.is_active ? (
                                  <button
                                    onClick={() => handleApprove(u.user_id)}
                                    disabled={!!approving[u.user_id]}
                                    className="bg-emerald-500 text-white active:bg-emerald-600 text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-2 ease-linear transition-all duration-150 disabled:opacity-60"
                                    type="button"
                                  >
                                    {approving[u.user_id]
                                      ? "Approving..."
                                      : "Approve"}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleDeactivate(u.user_id)}
                                    disabled={!!approving[u.user_id]}
                                    className="bg-red-500 text-white active:bg-red-600 text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-2 ease-linear transition-all duration-150 disabled:opacity-60"
                                    type="button"
                                  >
                                    {approving[u.user_id]
                                      ? "Updating..."
                                      : "Deactivate"}
                                  </button>
                                )}
                                <Tooltip title="Edit" arrow>
                                  <motion.button
                                    whileHover={{ scale: 1.2 }}
                                    whileTap={{ scale: 0.9 }}
                                    type="button"
                                    onClick={() => startEdit(u)}
                                    className="focus:outline-none"
                                    style={{ color: 'var(--accent-primary)' }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faPenToSquare}
                                      className="text-xl"
                                    />
                                  </motion.button>
                                </Tooltip>
                                <Tooltip title="Delete" arrow>
                                  <motion.button
                                    whileHover={{ scale: 1.2 }}
                                    whileTap={{ scale: 0.9 }}
                                    type="button"
                                    onClick={() => openDeleteModal(u)}
                                    className="focus:outline-none ml-3"
                                    style={{ color: '#ef4444' }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      className="text-xl"
                                    />
                                  </motion.button>
                                </Tooltip>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              {!loading &&
                !error &&
                Array.isArray(users) &&
                users.length === 0 && (
                  <div className="text-white">No users found.</div>
                )}
            </div>
          </div>
        </div>
      </div>
      {/* dropdown panel is rendered inline above; modal removed */}
      
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
          <div className="fixed inset-0 bg-black opacity-50"></div>
          <div className="relative w-auto max-w-lg mx-auto my-6 z-50">
            <div className="relative flex flex-col w-full bg-blueGray-800 border-0 rounded-lg shadow-lg outline-none focus:outline-none border border-blueGray-600">
              <div className="flex items-start justify-between p-5 border-b border-solid rounded-t border-blueGray-600">
                <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
                <button
                  className="p-1 ml-auto bg-transparent border-0 text-white opacity-50 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                  onClick={closeDeleteModal}
                >
                  <span className="bg-transparent text-white opacity-50 h-6 w-6 text-2xl block outline-none focus:outline-none">×</span>
                </button>
              </div>
              <div className="relative p-6 flex-auto">
                <p className="my-4 text-white text-lg leading-relaxed">
                  Are you sure you want to delete <span className="font-bold">{userToDelete?.first_name} {userToDelete?.last_name}</span>? 
                  <br/><br/>
                  <span className="text-red-400 font-semibold">Warning: All data related with that user will be lost like inventory, event, facility, project. This action cannot be undone.</span>
                </p>
              </div>
              <div className="flex items-center justify-end p-6 border-t border-solid rounded-b border-blueGray-600">
                <button
                  className="text-white bg-blueGray-500 font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-3 mb-1 ease-linear transition-all duration-150 rounded"
                  type="button"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
                <button
                  className="bg-red-600 text-white font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:opacity-50"
                  type="button"
                  onClick={confirmDeleteUser}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Delete Permanently"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
